import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API, callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { setUser } from './authSlice';

const loadUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem('userData');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
  } catch (error) {
    console.error('Failed to load user from localStorage', error);
  }
  return null;
};
const storedUserData = loadUserFromStorage();

const initialState = {
  userData: storedUserData || {
    full_name: "",
    phone: "",
    email: "",
    adress: []
  },

  passwordData: {
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  },

  isEditingDetails: false,
  isEditingAddress: false,
  isEditingPassword: false,
  isDeleting: false,

  loading: false,
  profileLoading: false,
  addressLoading: false,
  passwordLoading: false,
  deleteLoading: false,
  imageUploading: false,

  message: {
    type: "",
    text: ""
  },

  error: null,

  profileUpdateSuccess: false,
  addressUpdateSuccess: false,
  passwordChangeSuccess: false,
  accountDeleted: false
};

export const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.getUser,
        {},
        "GET",
        {}
      );
      if (response.data.success) {
        dispatch(setUser(response.data.data));
        return response.data.data;
      }
      else {
        return rejectWithValue(response?.data?.message)
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user profile');
    }
  }
);

export const updateProfileImage = createAsyncThunk(
  'user/updateProfileImage',
  async (formData, { dispatch, rejectWithValue }) => {
    try {
      const response = await API(
        apiUrls.updateImage,
        {},
        "POST",
        formData,
      );

      if (response.data.success) {
        const userResponse = await callAPI(
          apiUrls.getUser,
          {},
          "GET",
          {}
        );
        
        if (userResponse.data.success) {
          dispatch(setUser(userResponse.data.data));
        }

        return {
          success: true,
          image_url: response.data.data?.image_url,
          message: response.data.message || 'Profile image updated successfully'
        };
      } else {
        return rejectWithValue(response.data?.message || 'Failed to update profile image');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile image');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.editProfile,
        {},
        "POST",
        {
          full_name: userData?.full_name,
          phone: parseInt(userData?.phone),
          email: userData?.email
        }
      );

      if (response.data.success) {
        const userResponse = await callAPI(
          apiUrls.getUser,
          {},
          "GET",
          {}
        );
        
        if (userResponse.data.success) {
          dispatch(setUser(userResponse.data.data));
        }

        return {
          full_name: userData?.full_name,
          phone: userData?.phone,
          email: userData?.email,
          message: response.data.message || 'Profile updated successfully!'
        };
      } else {
        return rejectWithValue(response.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const updateUserAddress = createAsyncThunk(
  'user/updateUserAddress',
  async (addressData, { dispatch, rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.addressUpdate,
        {},
        "POST",
        {
          address: addressData?.address,
          city: addressData?.city,
          state: addressData?.state,
          country: addressData?.country,
          zipcode: addressData?.zipcode,
          address_line2: addressData?.address_line2
        }
      );

      if (response.data.success) {
        const userResponse = await callAPI(
          apiUrls.getUser,
          {},
          "GET",
          {}
        );
        
        if (userResponse.data.success) {
          dispatch(setUser(userResponse.data.data));
        }

        return addressData;
      } else {
        return rejectWithValue(response.data?.message || 'Failed to update address');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update address');
    }
  }
);

export const changePassword = createAsyncThunk(
  'user/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.changePassword,
        {
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
          new_password_confirmation: passwordData.confirmPassword
        },
        "POST",
        {}
      );

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Password changed successfully!'
        };
      } else {
        return rejectWithValue(response.data?.message || 'Failed to change password');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password');
    }
  }
);

export const deleteUserAccount = createAsyncThunk(
  'user/deleteUserAccount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.deleteAccount,
        {},
        "GET",
        {}
      );
      if (response.data.success) {
        return response.data.message
      }
      else {
        return rejectWithValue(response?.data?.message || 'Failed to delete account');
      }

    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete account');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserDataField: (state, action) => {
      const { name, value } = action.payload;
      if (state.userData) {
        state.userData[name] = value;
        localStorage.setItem('userData', JSON.stringify(state.userData));
      }
    },

    setAddressField: (state, action) => {
      const { name, value } = action.payload;
      if (state.userData) {
        if (!state.userData.adress) {
          state.userData.adress = [{}];
        }
        if (!state.userData.adress[0]) {
          state.userData.adress[0] = {};
        }

        const fieldMapping = {
          line1: 'address',
          line2: 'address_line2',
          city: 'city',
          state: 'state',
          zipCode: 'zipcode',
          country: 'country'
        };

        const apiFieldName = fieldMapping[name] || name;
        state.userData.adress[0][apiFieldName] = value;
        localStorage.setItem('userData', JSON.stringify(state.userData));
      }
    },

    setPasswordField: (state, action) => {
      const { name, value } = action.payload;
      if (state.passwordData) {
        state.passwordData[name] = value;
      }
    },

    setIsEditingDetails: (state, action) => {
      state.isEditingDetails = action.payload;
    },

    setIsEditingAddress: (state, action) => {
      state.isEditingAddress = action.payload;
    },

    setIsEditingPassword: (state, action) => {
      state.isEditingPassword = action.payload;
    },

    setIsDeleting: (state, action) => {
      state.isDeleting = action.payload;
    },

    setMessage: (state, action) => {
      state.message = {
        type: action.payload.type,
        text: action.payload.text
      };
    },

    clearMessage: (state) => {
      state.message = {
        type: "",
        text: ""
      };
    },

    resetPasswordForm: (state) => {
      state.passwordData = {
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      };
    },

    resetUserData: (state, action) => {
      if (action.payload) {
        state.userData = action.payload;
        localStorage.setItem('userData', JSON.stringify(action.payload));
      }
    },

    clearProfileUpdateSuccess: (state) => {
      state.profileUpdateSuccess = false;
    },

    clearAddressUpdateSuccess: (state) => {
      state.addressUpdateSuccess = false;
    },

    clearPasswordChangeSuccess: (state) => {
      state.passwordChangeSuccess = false;
    },

    resetUserState: (state) => {
      state.userData = {
        full_name: "Travis Barker",
        phone: "9999999999",
        email: "travisbarker@gmail.com",
        adress: []
      };
      state.passwordData = {
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      };
      state.isEditingDetails = false;
      state.isEditingAddress = false;
      state.isEditingPassword = false;
      state.isDeleting = false;
      state.message = { type: "", text: "" };
      state.error = null;
      localStorage.removeItem('userData');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileLoading = false;
        state.userData = action.payload;
        localStorage.setItem('userData', JSON.stringify(action.payload));
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.profileLoading = false;
        state.error = action.payload || 'Failed to fetch user profile';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to fetch user profile'
        };
      })

      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.profileLoading = true;
        state.error = null;
        state.profileUpdateSuccess = false;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileLoading = false;

        if (action.payload) {
          if (action.payload.full_name) {
            state.userData.full_name = action.payload.full_name;
          }
          if (action.payload.phone) {
            state.userData.phone = action.payload.phone;
          }
          if (action.payload.email) {
            state.userData.email = action.payload.email;
          }
        }

        state.isEditingDetails = false;
        state.profileUpdateSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload?.message || 'Profile updated successfully!'
        };
        localStorage.setItem('userData', JSON.stringify(state.userData));
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.profileLoading = false;
        state.error = action.payload || 'Failed to update profile';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to update profile'
        };
      })

      .addCase(updateUserAddress.pending, (state) => {
        state.loading = true;
        state.addressLoading = true;
        state.error = null;
        state.addressUpdateSuccess = false;
      })
      .addCase(updateUserAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addressLoading = false;

        if (action.payload) {
          if (!state.userData.adress) {
            state.userData.adress = [];
          }

          if (state.userData.adress.length > 0) {
            state.userData.adress[0] = {
              ...state.userData.adress[0],
              ...action.payload
            };
          } else {
            state.userData.adress[0] = action.payload;
          }
        }

        state.isEditingAddress = false;
        state.addressUpdateSuccess = true;
        state.message = {
          type: 'success',
          text: 'Address updated successfully!'
        };
        localStorage.setItem('userData', JSON.stringify(state.userData));
      })
      .addCase(updateUserAddress.rejected, (state, action) => {
        state.loading = false;
        state.addressLoading = false;
        state.error = action.payload || 'Failed to update address';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to update address'
        };
      })
      .addCase(updateProfileImage.pending, (state) => {
        state.imageUploading = true;
        state.error = null;
      })
      .addCase(updateProfileImage.fulfilled, (state, action) => {
        state.imageUploading = false;
        if (action.payload.image_url) {
          state.userData = {
            ...state.userData,  
            image_url: action.payload.image_url,
            avatar: action.payload.image_url
          };


          localStorage.setItem('userData', JSON.stringify(state.userData));
        }

        state.message = {
          type: 'success',
          text: action.payload.message
        };
      })
      .addCase(updateProfileImage.rejected, (state, action) => {
        state.imageUploading = false;
        state.error = action.payload || 'Failed to update profile image';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to update profile image'
        };
      })

      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.passwordLoading = true;
        state.error = null;
        state.passwordChangeSuccess = false;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordLoading = false;
        state.passwordData = {
          oldPassword: "",
          newPassword: "",
          confirmPassword: ""
        };
        state.isEditingPassword = false;
        state.passwordChangeSuccess = true;
        state.message = {
          type: 'success',
          text: action.payload?.message || 'Password changed successfully!'
        };
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.passwordLoading = false;
        state.error = action.payload || 'Failed to change password';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to change password'
        };
      })

      .addCase(deleteUserAccount.pending, (state) => {
        state.loading = true;
        state.deleteLoading = true;
        state.error = null;
        state.accountDeleted = false;
      })
      .addCase(deleteUserAccount.fulfilled, (state) => {
        state.loading = false;
        state.deleteLoading = false;
        state.isDeleting = false;
        state.accountDeleted = true;
        state.message = {
          type: 'success',
          text: 'Account deleted successfully'
        };
        localStorage.removeItem('userData');
        localStorage.removeItem('accessToken');
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.loading = false;
        state.deleteLoading = false;
        state.isDeleting = false;
        state.error = action.payload || 'Failed to delete account';
        state.message = {
          type: 'error',
          text: action.payload || 'Failed to delete account'
        };
      });
  }
});

export const {
  setUserDataField,
  setAddressField,
  setPasswordField,
  setIsEditingDetails,
  setIsEditingAddress,
  setIsEditingPassword,
  setIsDeleting,
  setMessage,
  clearMessage,
  resetPasswordForm,
  resetUserData,
  clearProfileUpdateSuccess,
  clearAddressUpdateSuccess,
  clearPasswordChangeSuccess,
  resetUserState
} = userSlice.actions;

export default userSlice.reducer;